import BkErrorMessage from '../../../custom-elements/bk/form-validation/bk-error-message/bk-error-message';
import BkErrorLog from '../../bk/bk-error-log.js';

/**
 * Converts the "message" property of an Error object into a formatted string.
 * @param {Error} error - The Error object.
 * @param {string} [key='message'] - The property key to extract the value from the Error object (default: 'message').
 * @returns {string} The formatted error string.
 */
const errorToString = function (error, key = 'message') {
    if (error === null || (typeof error !== 'object' && typeof error !== 'function')) {
        return '';
    }

    let val = error[key];
    val = val === undefined ? '' : `${val}`;

    return `${val}`;
};

/**
 * Finds the error display element within a given element or the document.
 * @param {Element} [element=document] - The element to search for the error display element (optional).
 * @returns {BkErrorMessage|null} The error display element, or null if not found.
 */
const findErrorDisplayElement = (element = document) => {
    const errorDisplayElement = element.querySelector('bk-error-message');
    if (errorDisplayElement instanceof BkErrorMessage) {
        return errorDisplayElement;
    } else {
        console.log('Element to display error message text not found!');

        return null;
    }
};

/**
 * Displays an error message using the error display element.
 * To define new error types (used for title texts), please go to bk-error-message.js
 * @param {Error} error - The error object to log.
 * @param {Element} [element] - html element that contains the bk-error-message component that should be displayed (optional parameter because sometimes the page has multiple error displays i.e. on overview page and its modal). if parameter is not given, the first found bk-error-message will be used (if present).
 * @param {string} [displayMessage] - The error message to display. if not given, the message property of the Error will be displayed
 */
export const displayError = (error, element, displayMessage) => {
    const statusCode = errorToString(error, 'statusCode');
    let errorType = 'default';
    switch (statusCode) {
        case '400':
            errorType = 'exception';
            break;
        case '403':
            errorType = 'denied';
            break;
    }

    const errorDisplayElement = findErrorDisplayElement(element);
    if (errorDisplayElement) {
        errorDisplayElement.triggerError(displayMessage || errorToString(error), errorType);
    } else {
        console.error(error);
    }
    document.dispatchEvent(new CustomEvent('bk-stop-loading'));
};

/**
 * Displays an error message and logs the error.
 * @param {Error} error - The error object to log.
 * @param {string} [displayMessage] - The error message to display.
 * @param {Element} [element] - The element to search for the error display element.
 */
export const displayErrorAndLog = (error, displayMessage, element) => {
    displayError(error, element, displayMessage);

    // BE - Log
    const logger = new BkErrorLog();
    logger.error(`Error (displayErrorAndLog - bk-error.js): ${errorToString(error)}`);
};

/**
 * Logs the error.
 * @param {Error} error - The error object to log.
 */
export const logError = (error) => {
    // Stop loader
    document.dispatchEvent(new CustomEvent('bk-stop-loading'));

    // BE - Log
    const logger = new BkErrorLog();
    logger.error(`Error (displayErrorAndLog - bk-error.js): ${errorToString(error)}`);
};

/**
 * Handles the error response from a fetch request, displays the error message,
 * logs the error, and dispatches a 'bk-stop-loading' event.
 * @param {Response} response - The response object from the fetch request.
 * @param {Element} [element] - The element to search for the error display element.
 */
export const handleFetchErrorEvent = (response, element) => {
    response
        .json()
        .then((data) => {
            displayErrorAndLog(data, data.message, element);
        })
        .catch((error) => {
            const errorMessage = 'Network response was not successful.';
            displayErrorAndLog(error, errorMessage, element);
        });
};
